// Plain-JS checks on pixel data: brightness, blur and glare, plus a file
// hash for spotting a re-sent photo.

export const toGrey = (imageData) => {
  const { data, width, height } = imageData;
  const grey = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    grey[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return { grey, width, height };
};

// Area to measure: the detected card box, or the whole image.
const regionOf = (width, height, box) =>
  box || { x: 0, y: 0, w: width, h: height };

export const measureBrightness = ({ grey, width, height }, box) => {
  const r = regionOf(width, height, box);
  let sum = 0;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) sum += grey[y * width + x];
  }
  return sum / (r.w * r.h);
};

// Variance of the Laplacian: sharp images have strong edges, so a high
// spread of values. Blurry images score low.
export const measureBlur = ({ grey, width, height }, box) => {
  const r = regionOf(width, height, box);
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = Math.max(1, r.y); y < Math.min(height - 1, r.y + r.h); y++) {
    for (let x = Math.max(1, r.x); x < Math.min(width - 1, r.x + r.w); x++) {
      const i = y * width + x;
      const lap =
        grey[i - width] +
        grey[i + width] +
        grey[i - 1] +
        grey[i + 1] -
        4 * grey[i];
      sum += lap;
      sumSq += lap * lap;
      n++;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
};

// Share of near-white pixels overall, and the worst cell of an 8x8 grid
// (glare is usually one concentrated patch).
export const measureGlare = ({ grey, width, height }, box, pixelValue) => {
  const r = regionOf(width, height, box);
  const GRID = 8;
  const cellW = r.w / GRID;
  const cellH = r.h / GRID;
  const cells = new Array(GRID * GRID).fill(0);
  const cellTotals = new Array(GRID * GRID).fill(0);
  let bright = 0;

  for (let y = r.y; y < r.y + r.h; y++) {
    const cy = Math.min(GRID - 1, Math.floor((y - r.y) / cellH));
    for (let x = r.x; x < r.x + r.w; x++) {
      const c = cy * GRID + Math.min(GRID - 1, Math.floor((x - r.x) / cellW));
      cellTotals[c]++;
      if (grey[y * width + x] >= pixelValue) {
        bright++;
        cells[c]++;
      }
    }
  }

  const worstCell = Math.max(
    ...cells.map((count, i) => (cellTotals[i] ? count / cellTotals[i] : 0))
  );
  return { ratio: bright / (r.w * r.h), worstCell };
};

// SHA-256 of the file bytes, to spot the same file being sent again.
// A near-match hash is not used: a retake of the same card on the same
// table looks almost identical at low resolution. Returns null where
// crypto.subtle is missing (plain http on a LAN address).
export const fileHash = async (blob) => {
  if (!window.crypto || !window.crypto.subtle) return null;
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    await blob.arrayBuffer()
  );
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
};
